// src/pages/Products.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  useMediaQuery,
  Backdrop,
  CircularProgress,
  TextField,
  Divider,
  LinearProgress,
  Paper,
  Chip,
  Stack,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import * as XLSX from "xlsx";
import { exportProductsSpreadsheet, fetchData } from "../../api/client";
import { getSelectedCompanyIds, getActiveWorkspaceSlug } from "../../utils/workspaceStorage";
import { useProducts } from "../../hooks/useProducts";
import useCentralLinkedImportJob, {
  formatCentralLinkedDuration,
} from "../../hooks/useCentralLinkedImportJob";
import ProductTable from "../../components/ProductTable";
import AddProductForm from "../../components/product/AddProductForm/AddProductForm";
import AddTrechoForm from "../../components/product/AddTrechoForm/AddTrechoForm";
import { usePageContext } from "../../context/PageContext";
import { useAuthContext } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

import ProductsToolbar from "../../components/Products/ProductsToolbar";
import ProductImageAuditDialog from "../../components/Products/ProductImageAuditDialog";
import ImportPassagensErpDialog from "../../components/Products/ImportPassagensErpDialog";
import ImportDialog from "../../components/Products/ImportDialog";
import ImportGuideDialog from "../../components/Products/ImportGuideDialog";
import PasteDialog from "../../components/Products/PasteDialog";
import CentralImportDialog from "../../components/Products/CentralImportDialog";
import ConfirmDialog from "../../components/common/ConfirmDialog";

import {
  normalizeRow,
  n,
  brToBool,
  findAmbiguousFields,
  defaultColumnChoices,
  dropEmptyColumns,
  listFilledSourceFields,
} from "../../utils/importNormalize";
import { skuStorefrontError } from "../../utils/skuStorefront";
import { parsePasteBlock } from "../../utils/pasteParser";
import { ensureCategoriesByNameBatch } from "../../utils/ensureCategories";
import { useS3Upload } from "../../hooks/useS3Upload";
// Permissões
import { useEffectivePermissions, toSet, can } from "../../hooks/useEffectivePermissions";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import { useWorkspaceConfig } from "../../context/WorkspaceConfigContext";
import { WorkspaceSegments } from "../../constants/workspaceSegments";
import { productsPageTitleI18nKey } from "../../utils/workspaceSegmentLabels";
import {
  passagensByKeyFromConfigurationsResponse,
  passagensPartnerConfiguredInByKey,
} from "../../constants/passagensConfigHelpers";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

/** Parceiros com sincronização ERP direta no legado (`importarprodutosservicos`). */
const PASSAGENS_ERP_MVP_PARTNERS = Object.freeze([
  "praxxio",
  "distribusion",
  "totalbus",
  "rodosoft",
  "buson",
]);

/* ===========================================================
   Helpers para imagens externas → S3 (mesmo fluxo do form)
   =========================================================== */

// separa URLs por ; , espaço e quebra de linha
const splitImageUrls = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  const s = String(raw).trim();
  if (!s) return [];
  return s
    .split(/[;,|\n\r]+/g)
    .map((u) => u.trim())
    .filter(Boolean);
};

// tenta “normalizar” alguns encurtadores/conteúdos
const normalizeDownloadUrl = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("drive.google.com")) {
      let id = null;
      const m = u.pathname.match(/\/file\/d\/([^/]+)/i);
      if (m && m[1]) id = m[1];
      if (!id && u.searchParams.get("id")) id = u.searchParams.get("id");
      if (id) return `https://drive.google.com/uc?export=download&id=${id}`;
    }
    if (u.hostname.includes("dropbox.com")) {
      u.searchParams.set("dl", "1");
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
};

const extractDriveId = (url) => {
  try {
    const u = new URL(url);
    if (!u.hostname.includes("drive.google.com")) return null;
    const m = u.pathname.match(/\/file\/d\/([^/]+)/i);
    if (m?.[1]) return m[1];
    return u.searchParams.get("id") || null;
  } catch {
    return null;
  }
};

/** Uma foto entra uma vez: mesmo link ou mesmo arquivo do Drive. */
const dedupeImageUrls = (urls = []) => {
  const seen = new Set();
  const out = [];
  for (const raw of urls) {
    const src = normalizeDownloadUrl(String(raw || "").trim());
    if (!src) continue;
    const driveId = extractDriveId(src);
    const key = driveId ? `drive:${driveId}` : src;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(src);
  }
  return out;
};

const guessNameFromUrl = (url, index = 0) => {
  try {
    const u = new URL(url);
    const base = u.pathname.split("/").pop() || `img_${index + 1}`;
    return base.split("?")[0] || `img_${index + 1}`;
  } catch {
    return `img_${index + 1}`;
  }
};

const buildPrefix = (workspace = "default") => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `workspace-${workspace}/products/${yyyy}/${mm}`;
};


/** Converte ids_projetos de um produto (CSV/JSON/whatever) -> primeiro id numérico ou null */
const parseFirstProjectFromIds = (ids) => {
  if (!ids) return null;
  const cleaned = String(ids).replace(/[[\]\s]/g, "");
  const first = cleaned.split(",").find(Boolean);
  if (!first) return null;
  const nVal = Number(first);
  return Number.isFinite(nVal) && nVal > 0 ? nVal : null;
};

/** === Validação básica (espelha o back createProduct.validateRequired) === */
const validateRowRequired = (row) => {
  const errors = [];
  if (!row?.nome || String(row.nome).trim() === "") {
    errors.push("Nome é obrigatório.");
  }
  // Estoque deixa de ser obrigatório: quando não vier, define como 0
  if (row?.estoque === "" || row?.estoque == null) {
    row.estoque = 0;
  }
  if (row?.preco === "" || row?.preco == null) {
    errors.push("Preço é obrigatório.");
  }
  return errors;
};

/** Monta mapa SKU+projeto -> produto existente */
const buildExistingSkuProjectMap = (products) => {
  const map = new Map();
  if (!Array.isArray(products)) return map;

  for (const p of products) {
    const sku = (p?.sku || "").toString().trim();
    if (!sku) continue;
    const projId = parseFirstProjectFromIds(p?.ids_projetos);
    const key = `${sku}|${projId ?? "null"}`;
    if (!map.has(key)) {
      map.set(key, p);
    }
  }
  return map;
};

/** === NOVO: normaliza header === */
const normHeader = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/** === NOVO: pega coluna por aliases === */
const pickCol = (row, aliases = []) => {
  if (!row || typeof row !== "object") return undefined;
  const keys = Object.keys(row);
  const map = new Map(keys.map((k) => [normHeader(k), k]));
  for (const a of aliases) {
    const hit = map.get(normHeader(a));
    if (hit) return row[hit];
  }
  return undefined;
};

/** === NOVO: estoque como inteiro >= 0 === */
const toStockInt = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const normalized = s.replace(/\./g, "").replace(",", ".");
  const num = Number(normalized);
  if (!Number.isFinite(num)) return null;
  const asInt = Math.floor(num);
  return asInt < 0 ? 0 : asInt;
};

const toPriceFloat = (v) => {
  if (v === null || v === undefined) return null;

  // Se já é number (Excel quase sempre envia assim)
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return null;
    return v;
  }

  let s = String(v).trim();
  if (!s) return null;

  // remove símbolo de moeda e espaços
  s = s.replace(/[^\d.,-]/g, "");

  const hasComma = s.includes(",");
  const hasDot = s.includes(".");

  // Caso 1: 1.234,56  (pt-BR)
  if (hasComma && hasDot && s.lastIndexOf(",") > s.lastIndexOf(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  }
  // Caso 2: 1,234.56 (en-US)
  else if (hasComma && hasDot && s.lastIndexOf(".") > s.lastIndexOf(",")) {
    s = s.replace(/,/g, "");
  }
  // Caso 3: 1234,56 (pt-BR sem milhar)
  else if (hasComma && !hasDot) {
    s = s.replace(",", ".");
  }
  // Caso 4: 1234.56 (en-US) → não mexe

  const num = Number(s);
  if (!Number.isFinite(num)) return null;

  return num;
};

/** === NOVO: pool simples pra limitar concorrência === */
async function asyncPool(limit, items, iteratorFn) {
  const ret = [];
  const executing = [];
  for (const item of items) {
    const p = Promise.resolve().then(() => iteratorFn(item));
    ret.push(p);

    if (limit <= items.length) {
      const e = p.then(() => executing.splice(executing.indexOf(e), 1));
      executing.push(e);
      if (executing.length >= limit) {
        await Promise.race(executing);
      }
    }
  }
  return Promise.allSettled(ret);
}

/**  deixa o browser “respirar” pra renderizar loading/mensagens */
const yieldToUI = () => new Promise((r) => setTimeout(r, 0));

/**  formata número pt-BR */
const fmtInt = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return v.toLocaleString("pt-BR");
};

/** formata data/hora em pt-BR a partir de string/Date */
const fmtDateTimeBr = (value) => {
  if (!value) return "—";
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("pt-BR");
  } catch {
    return String(value);
  }
};


const Products = () => {
  const { t: tSettings } = useTranslation("settings");
  const { t: tProducts } = useTranslation("products");
  const pageCtx = usePageContext();
  const { currentPage, setCurrentPage } = pageCtx;
  const { userData } = useAuthContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pageSize, setPageSize] = useState(50);
  /** Ordenação da tabela (MUI DataGrid); sincronizada com GET /products?sort_by=&sort_dir= */
  const [productSortModel, setProductSortModel] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Exclusão
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isProcessingDelete, setIsProcessingDelete] = useState(false);

  // Importação planilha
  const [importGuideOpen, setImportGuideOpen] = useState(false);
  const [importReading, setImportReading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importRawRows, setImportRawRows] = useState([]);
  const [importAmbiguous, setImportAmbiguous] = useState([]);
  const [importChoices, setImportChoices] = useState({});
  const [importFilledFields, setImportFilledFields] = useState([]);
  const [importApplying, setImportApplying] = useState(false);
  const [importRemapping, setImportRemapping] = useState(false);

  // Colagem
  const [pasteOpen, setPasteOpen] = useState(false);
  const [imageAuditOpen, setImageAuditOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pasteText, setPasteText] = useState("");
  const [pastePreview, setPastePreview] = useState({ rowsValid: [], rowsInvalid: [] });
  const [pasteApplying, setPasteApplying] = useState(false);

  // Cadastro Central
  const [centralOpen, setCentralOpen] = useState(false);
  const [centralLoading, setCentralLoading] = useState(false);
  const [centralProgressDetail, setCentralProgressDetail] = useState("");
  const [hasLinkedWorkspaces, setHasLinkedWorkspaces] = useState(false);
  const [linkedWorkspacesChecking, setLinkedWorkspacesChecking] = useState(false);
  const [auditDialog, setAuditDialog] = useState({ open: false, items: [] });
  const [auditLinkedLoading, setAuditLinkedLoading] = useState(false);
  const [linkedWsSyncDialogOpen, setLinkedWsSyncDialogOpen] = useState(false);
  const [linkedWsSyncMode, setLinkedWsSyncMode] = useState("full");
  const [importPassagensDialogOpen, setImportPassagensDialogOpen] = useState(false);

  //  Atualização de preço por planilha
  const [priceConfirm, setPriceConfirm] = useState(null);
  /*
  priceConfirm shape:
  {
    fileName,
    stats: {...},
    candidates: [{ id, sku, sheetPrice, currentPrice }],
    notFoundSkus: string[],
  }
  */

  const [priceApplying, setPriceApplying] = useState(false);

  // feedback enquanto analisa planilha
  const [priceAnalyzing, setPriceAnalyzing] = useState(false);
  const [, setPriceAnalyzeStep] = useState("");


  //  Atualização de estoque por planilha
  const [stockConfirm, setStockConfirm] = useState(null);
  /**
   stockConfirm shape:
  {
    fileName,
    stats: {...},
    candidates: [{ id, sku, sheetStock, currentStock }], // <-- bruto
    notFoundSkus: string[],
  }
  */

  const [stockApplying, setStockApplying] = useState(false);

  //  feedback enquanto analisa planilha
  const [stockAnalyzing, setStockAnalyzing] = useState(false);
  const [stockAnalyzeStep, setStockAnalyzeStep] = useState("");

  //  ajuste no estoque (subtrair da planilha)
  const [stockAdjustment, setStockAdjustment] = useState(2);

  const stockComputed = useMemo(() => {
    if (!stockConfirm?.candidates?.length) {
      return { toUpdate: [], unchangedSkipped: 0 };
    }

    const adj = Number(stockAdjustment);
    const safeAdj = Number.isFinite(adj) ? Math.max(0, Math.floor(adj)) : 0;

    const toUpdate = [];
    let unchangedSkipped = 0;

    for (const it of stockConfirm.candidates) {
      const finalStock = Math.max(0, Number(it.sheetStock || 0) - safeAdj);

      const current =
        it.currentStock === "" || it.currentStock == null
          ? null
          : Number(it.currentStock);

      if (current !== null && Number.isFinite(current) && current === finalStock) {
        unchangedSkipped++;
        continue;
      }

      toUpdate.push({ ...it, finalStock });
    }

    return { toUpdate, unchangedSkipped };
  }, [stockConfirm, stockAdjustment]);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const selectedCompanyIds = useMemo(() => getSelectedCompanyIds(), []);

  // padrão do sistema: ids_projetos = empresas selecionadas
  const idsProjetos = useMemo(() => {
    return selectedCompanyIds.length
      ? selectedCompanyIds.join(",")
      : null;
  }, [selectedCompanyIds]);

  const productSortParams = useMemo(() => {
    const s = productSortModel?.[0];
    if (!s?.field || !s?.sort) return {};
    return { sort_by: s.field, sort_dir: s.sort };
  }, [productSortModel]);

  // quando precisa de id_projeto único (create/update)
  const firstProjectId = useMemo(() => {
    return selectedCompanyIds.length
      ? selectedCompanyIds[0]
      : null;
  }, [selectedCompanyIds]);

  // Verifica se o projeto selecionado possui regiões (workspaces vinculados)
  useEffect(() => {
    let cancelled = false;

    const checkRegions = async () => {
      if (!firstProjectId) {
        setHasLinkedWorkspaces(false);
        return;
      }
      try {
        setLinkedWorkspacesChecking(true);
        const res = await fetchData(`/companies/${firstProjectId}`, "GET");
        const payload = res?.data || res || {};
        const regioes = payload?.regioes || payload?.data?.regioes || [];
        if (!cancelled) {
          setHasLinkedWorkspaces(Array.isArray(regioes) && regioes.length > 0);
        }
      } catch {
        if (!cancelled) {
          setHasLinkedWorkspaces(false);
        }
      } finally {
        if (!cancelled) {
          setLinkedWorkspacesChecking(false);
        }
      }
    };

    checkRegions();

    return () => {
      cancelled = true;
    };
  }, [firstProjectId]);

  /** === PERMISSÕES === */
  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(() => toSet(permData?.scopes), [permData?.scopes]);

  const canCreateProduct = can(scopeSet, "products", "create");
  const canUpdateProduct = can(scopeSet, "products", "update");
  const canDeleteProduct = can(scopeSet, "products", "delete");

  const { segment } = useWorkspaceSegment();
  const { items: workspaceConfigItems } = useWorkspaceConfig();

  const passagensByKey = useMemo(
    () => passagensByKeyFromConfigurationsResponse({ data: { items: workspaceConfigItems } }),
    [workspaceConfigItems]
  );

  const isPassengerTransport = segment === WorkspaceSegments.PassengerTransport;

  const showImportPassagensErp = useMemo(() => {
    if (!isPassengerTransport) return false;
    if (!canCreateProduct) return false;
    return PASSAGENS_ERP_MVP_PARTNERS.some((p) => passagensPartnerConfiguredInByKey(p, passagensByKey));
  }, [isPassengerTransport, canCreateProduct, passagensByKey]);

  // Hook principal da listagem
  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    createMutation,
    isLoading,
    isFetching,
    getById,
  } = useProducts(currentPage, pageSize, search, {
    ids_projetos: idsProjetos,
    ...productSortParams,
  });

  const editProductId = searchParams.get("edit");
  useEffect(() => {
    if (!editProductId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const full = await getById(editProductId);
        if (!cancelled && full) setSelectedProduct(full);
      } catch {
        /* id inválido: não abre o cadastro */
      } finally {
        if (!cancelled) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete("edit");
              return next;
            },
            { replace: true }
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // getById é recriado a cada render; o efeito só pode depender do id da URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProductId]);

  const centralLinkedDialogOpenRef = React.useRef(false);

  const {
    job: centralLinkedJob,
    summary: bulkCentralSummary,
    loading: bulkCentralLoading,
    dialogOpen: bulkCentralDialogOpen,
    elapsedMs: bulkCentralElapsedMs,
    estimatedRemainingMs: bulkCentralEtaMs,
    overallProgress: bulkCentralOverallProgress,
    startJob: startCentralLinkedImportJobFlow,
    cancelJob: cancelCentralLinkedImportJobFlow,
    openProgressDialog,
    closeProgressDialog,
    setSummary: setBulkCentralSummary,
  } = useCentralLinkedImportJob({
    sourceProjectId: firstProjectId,
    onCompleted: async () => {
      await refetch();
    },
    onTerminal: (job, status) => {
      const ok = Number(job?.succeededCount) || 0;
      const fail = Number(job?.failedCount) || 0;

      if (status === "cancelled") {
        setSnack({
          open: true,
          severity: "info",
          message: tProducts("linkedWsDialog.cancelled"),
        });
        return;
      }

      if (status === "failed") {
        setSnack({
          open: true,
          severity: "error",
          message: job?.errorMessage || tProducts("linkedWsDialog.failed"),
        });
        return;
      }

      if (centralLinkedDialogOpenRef.current) return;

      if (status === "completed") {
        setSnack({
          open: true,
          severity: fail ? "warning" : "success",
          message: fail
            ? `Workspaces vinculados: ${ok} atualizada(s), ${fail} com erro.`
            : `Workspaces vinculados: ${ok} empresa(s)/projeto(s) atualizada(s).`,
        });
      }
    },
  });

  useEffect(() => {
    centralLinkedDialogOpenRef.current = bulkCentralDialogOpen;
  }, [bulkCentralDialogOpen]);

  // Hook adicional, usado só para pré-validação de SKUs da importação
  const { data: importProductsData } = useProducts(1, 10000, "", { ids_projetos: idsProjetos });

  // Mapa SKU+projeto -> produto existente, vindo DO HOOK de produtos
  const existingSkuProjectMapFromHook = useMemo(
    () => buildExistingSkuProjectMap(importProductsData?.products || []),
    [importProductsData]
  );

  // upload S3 (mesmo hook do formulário manual) – mantido caso use futuramente
  useS3Upload();

  useEffect(() => {
    if (error) setErrorMessage(error?.message || "Erro ao carregar produtos. Tente novamente.");
    else setErrorMessage(null);
  }, [error]);

  /**
   * Lê a planilha, normaliza, resolve categorias e
   * faz PRÉ-VALIDAÇÃO:
   * - Erros (vermelho)
   * - Atualizações (amarelo) → id presente OU SKU+projeto já existe
   * - Inclusões (verde)
   */
  const prepareImportRows = async (json, choices = {}) => {
    const normalized = json.map((row) => normalizeRow(row, choices));

    const pairs = normalized
      .filter(
        (r) =>
          !r.id_categoria &&
          !r.id_subcategoria &&
          (r.categoria_nome || r.subcategoria_nome)
      )
      .map((r) => ({
        categoria: r.categoria_nome || "",
        subcategoria: r.subcategoria_nome || "",
      }));

    const keyOf = (c, s) => `${String(c || "").trim()}|${String(s || "").trim()}`;
    const resolved = await ensureCategoriesByNameBatch(pairs);

    normalized.forEach((r) => {
      if (!r.id_categoria && !r.id_subcategoria) {
        const k = keyOf(r.categoria_nome, r.subcategoria_nome);
        const ids = resolved.get(k);
        if (ids) {
          r.id_categoria = ids.id_categoria;
          r.id_subcategoria = ids.id_subcategoria;
        }
      }
    });

    const existingSkuProjectMap = existingSkuProjectMapFromHook || new Map();
    const idCounts = new Map();
    const skuProjCounts = new Map();
    const projIdForImport = firstProjectId ?? null;

    for (const r of normalized) {
      const idStr = r.id != null && r.id !== "" ? String(r.id).trim() : null;
      if (idStr) {
        idCounts.set(idStr, (idCounts.get(idStr) || 0) + 1);
      }

      const sku = (r.sku || "").toString().trim();
      if (sku) {
        const key = `${sku}|${projIdForImport ?? "null"}`;
        skuProjCounts.set(key, (skuProjCounts.get(key) || 0) + 1);
      }
    }

    return normalized.map((r) => {
      const errors = [];
      const idStr = r.id != null && r.id !== "" ? String(r.id).trim() : null;
      const sku = (r.sku || "").toString().trim();
      const keySkuProj = sku ? `${sku}|${projIdForImport ?? "null"}` : null;

      errors.push(...validateRowRequired(r));

      if (idStr && idCounts.get(idStr) > 1) {
        errors.push("ID duplicado na planilha.");
      }
      if (keySkuProj && skuProjCounts.get(keySkuProj) > 1) {
        errors.push("SKU duplicado para o mesmo projeto na planilha.");
      }

      let existingProductId = null;
      if (!idStr && keySkuProj && existingSkuProjectMap.has(keySkuProj)) {
        const existing = existingSkuProjectMap.get(keySkuProj);
        existingProductId = existing?.id ?? null;
      }

      if (sku && !idStr && !existingProductId) {
        const skuErr = skuStorefrontError(sku);
        if (skuErr) errors.push(skuErr);
      }

      let status = "new";
      if (errors.length) {
        status = "error";
      } else if (idStr || existingProductId) {
        status = "update";
      }

      return {
        ...r,
        __status: status,
        __errors: errors,
        __existingProductId: existingProductId,
      };
    });
  };

  const handlePickImport = (file) => {
    if (!file) return;

    setStockAdjustment(2);
    setImportReading(true);

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const binary = evt.target.result;
        const wb = XLSX.read(binary, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const json = dropEmptyColumns(XLSX.utils.sheet_to_json(ws, { defval: "" }));
        if (!json.length) {
          setSnack({
            open: true,
            severity: "error",
            message: tProducts("importGuide.emptySheet"),
          });
          return;
        }

        const ambiguous = findAmbiguousFields(json);
        const choices = defaultColumnChoices(ambiguous);
        const decorated = await prepareImportRows(json, choices);
        const filledFields = Array.from(listFilledSourceFields(json));

        setImportRawRows(json);
        setImportAmbiguous(ambiguous);
        setImportChoices(choices);
        setImportFilledFields(filledFields);
        setImportRows(decorated);
        setImportGuideOpen(false);
        setImportOpen(true);
      } catch (err) {
        console.error("[import] erro ao processar planilha:", err);
        setSnack({
          open: true,
          severity: "error",
          message: err?.message || "Falha ao processar planilha.",
        });
      } finally {
        setImportReading(false);
      }
    };
    reader.onerror = () => {
      setImportReading(false);
      setSnack({
        open: true,
        severity: "error",
        message: tProducts("importGuide.readError"),
      });
    };

    reader.readAsBinaryString(file);
  };

  const handleImportColumnChoice = async (field, header) => {
    if (importApplying || importRemapping) return;
    if (importChoices[field] === header) return;
    const next = { ...importChoices, [field]: header };
    setImportChoices(next);
    setImportRemapping(true);
    const started = Date.now();
    try {
      const decorated = await prepareImportRows(importRawRows, next);
      setImportRows(decorated);
    } catch (err) {
      setSnack({
        open: true,
        severity: "error",
        message: err?.message || "Falha ao aplicar a coluna escolhida.",
      });
    } finally {
      const wait = Math.max(0, 280 - (Date.now() - started));
      if (wait) await new Promise((resolve) => setTimeout(resolve, wait));
      setImportRemapping(false);
    }
  };

  const closeImportPreview = () => {
    if (importApplying || importRemapping) return;
    setImportOpen(false);
    setImportRawRows([]);
    setImportAmbiguous([]);
    setImportChoices({});
    setImportFilledFields([]);
    setImportRemapping(false);
  };

  /** Atualizar estoque por planilha (SKU + estoque) com feedback e resumo */
  const handlePickStockUpdate = (e) => {
    if (!firstProjectId) {
      setSnack({
        open: true,
        severity: "error",
        message: "Nenhuma empresa/projeto selecionado.",
      });
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;

    // default para decisão inicial (o usuário pode mudar no dialog depois)
    setStockAdjustment(2);

    const reader = new FileReader();

    reader.onload = async (evt) => {
      setStockAnalyzing(true);
      setStockAnalyzeStep("Lendo arquivo…");
      await yieldToUI();

      try {
        const binary = evt.target.result;

        setStockAnalyzeStep("Interpretando planilha…");
        await yieldToUI();

        const wb = XLSX.read(binary, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

        // 1) Monta map SKU -> estoque
        setStockAnalyzeStep("Validando colunas e montando mapa SKU → estoque…");
        await yieldToUI();

        const skuToStock = new Map();
        let invalidRows = 0;

        for (const r of rows) {
          const skuRaw = pickCol(r, ["sku", "SKU", "codigo", "código", "code", "PRODUTO"]);
          const stockRaw = pickCol(r, [
            "estoque",
            "stock",
            "quantidade",
            "qtd",
            "qty",
            ".DISP.(1)",
          ]);

          const sku = (skuRaw ?? "").toString().trim();
          const stockInt = toStockInt(stockRaw);

          if (!sku || stockInt === null) {
            invalidRows++;
            continue;
          }

          // SKU repetido: última linha vence
          skuToStock.set(sku, stockInt);
        }

        const totalRows = rows.length;
        const validRows = totalRows - invalidRows;
        const uniqueSkus = skuToStock.size;

        if (!uniqueSkus) {
          setSnack({
            open: true,
            severity: "error",
            message:
              "Planilha sem linhas válidas. Precisa ter colunas SKU e Estoque (ou aliases como stock/qtd).",
          });
          return;
        }

        // 2) Produtos existentes do banco
        setStockAnalyzeStep("Carregando produtos do banco para comparação…");
        await yieldToUI();

        const existingProducts = importProductsData?.products || [];
        if (!existingProducts.length) {
          setSnack({
            open: true,
            severity: "error",
            message:
              "Ainda não consegui carregar a lista de produtos para comparar. Aguarde a tela terminar de carregar e tente novamente.",
          });
          return;
        }

        // 3) Monta candidates (bruto) e métricas básicas (sem considerar ajuste aqui)
        setStockAnalyzeStep("Comparando SKUs e preparando candidatos…");
        await yieldToUI();

        const candidates = [];
        const foundSkus = new Set();

        for (const p of existingProducts) {
          const sku = (p?.sku || "").toString().trim();
          if (!sku) continue;
          if (!skuToStock.has(sku)) continue;

          foundSkus.add(sku);

          const sheetStock = skuToStock.get(sku);
          const currentStock =
            p?.estoque === "" || p?.estoque == null ? null : Number(p.estoque);

          candidates.push({
            id: p.id,
            sku,
            sheetStock,
            currentStock,
          });
        }

        const notFoundSkus = [];
        for (const sku of skuToStock.keys()) {
          if (!foundSkus.has(sku)) notFoundSkus.push(sku);
        }

        if (!candidates.length) {
          setSnack({
            open: true,
            severity: "warning",
            message:
              "Nenhum SKU da planilha foi encontrado no cadastro (projeto atual).",
          });
          return;
        }

        // 4) Preview: quantos vão atualizar considerando o ajuste atual (default 2 agora)
        const adj = Number(stockAdjustment);
        const safeAdjLocal = Number.isFinite(adj) ? Math.max(0, Math.floor(adj)) : 0;

        let willUpdate = 0;
        for (const it of candidates) {
          const finalStock = Math.max(0, Number(it.sheetStock || 0) - safeAdjLocal);
          const current =
            it.currentStock === "" || it.currentStock == null
              ? null
              : Number(it.currentStock);

          if (current !== null && Number.isFinite(current) && current === finalStock) {
            continue;
          }
          willUpdate++;
        }

        const stats = {
          totalRows,
          invalidRows,
          validRows,
          uniqueSkus,
          productsLoaded: existingProducts.length,
          matchedSkus: foundSkus.size,
          candidatesCount: candidates.length,
          notFoundSkusCount: notFoundSkus.length,
        };

        setSnack({
          open: true,
          severity: "info",
          message: `Planilha analisada: ${fmtInt(stats.uniqueSkus)} SKUs válidos. Vou atualizar ${fmtInt(
            willUpdate
          )} produtos (projeto atual).`,
        });

        setStockConfirm({
          fileName: file.name,
          stats,
          candidates,
          notFoundSkus,
        });
      } catch (err) {
        console.error("[stock-update] erro ao processar planilha:", err);
        setSnack({
          open: true,
          severity: "error",
          message: err?.message || "Falha ao processar planilha de estoque.",
        });
      } finally {
        setStockAnalyzing(false);
        setStockAnalyzeStep("");
      }
    };

    reader.onerror = () => {
      setStockAnalyzing(false);
      setStockAnalyzeStep("");
      setSnack({
        open: true,
        severity: "error",
        message: "Falha ao ler o arquivo. Tente novamente.",
      });
    };

    setStockAnalyzing(true);
    setStockAnalyzeStep("Abrindo arquivo…");
    reader.readAsBinaryString(file);

    e.target.value = null;
  };

  const handlePickPriceUpdate = (e) => {
    if (!firstProjectId) {
      setSnack({
        open: true,
        severity: "error",
        message: "Nenhuma empresa/projeto selecionado.",
      });
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (evt) => {
      setPriceAnalyzing(true);
      setPriceAnalyzeStep("Lendo arquivo…");
      await yieldToUI();

      try {
        const binary = evt.target.result;

        setPriceAnalyzeStep("Interpretando planilha…");
        await yieldToUI();

        const wb = XLSX.read(binary, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

        // 1) Monta map SKU → preço
        setPriceAnalyzeStep("Validando colunas e montando mapa SKU → preço…");
        await yieldToUI();

        const skuToPrice = new Map();
        let invalidRows = 0;

        for (const r of rows) {
          const skuRaw = pickCol(r, ["sku", "SKU", "codigo", "código", "code", "PRODUTO"]);
          const priceRaw = pickCol(r, [
            "preco",
            "preço",
            "price",
            "valor",
            "unitario",
            "unit",
          ]);

          const sku = (skuRaw ?? "").toString().trim();
          const price = toPriceFloat(priceRaw);

          if (!sku || price === null) {
            invalidRows++;
            continue;
          }

          // SKU repetido: última linha vence
          skuToPrice.set(sku, price);
        }

        const totalRows = rows.length;
        const validRows = totalRows - invalidRows;
        const uniqueSkus = skuToPrice.size;

        if (!uniqueSkus) {
          setSnack({
            open: true,
            severity: "error",
            message:
              "Planilha sem linhas válidas. Precisa ter colunas SKU e Preço.",
          });
          return;
        }

        // 2) Produtos existentes
        setPriceAnalyzeStep("Carregando produtos do banco…");
        await yieldToUI();

        const existingProducts = importProductsData?.products || [];
        if (!existingProducts.length) {
          setSnack({
            open: true,
            severity: "error",
            message:
              "Ainda não consegui carregar os produtos para comparar.",
          });
          return;
        }

        // 3) Comparar
        setPriceAnalyzeStep("Comparando preços…");
        await yieldToUI();

        const candidates = [];
        const foundSkus = new Set();

        for (const p of existingProducts) {
          const sku = (p?.sku || "").toString().trim();
          if (!sku) continue;
          if (!skuToPrice.has(sku)) continue;

          foundSkus.add(sku);

          const sheetPrice = skuToPrice.get(sku);
          const currentPrice =
            p?.preco === "" || p?.preco == null ? null : Number(p.preco);

          candidates.push({
            id: p.id,
            sku,
            sheetPrice,
            currentPrice,
          });
        }

        const notFoundSkus = [];
        for (const sku of skuToPrice.keys()) {
          if (!foundSkus.has(sku)) notFoundSkus.push(sku);
        }

        if (!candidates.length) {
          setSnack({
            open: true,
            severity: "warning",
            message:
              "Nenhum SKU da planilha foi encontrado no cadastro.",
          });
          return;
        }

        let willUpdate = 0;
        for (const it of candidates) {
          if (it.currentPrice !== it.sheetPrice) willUpdate++;
        }

        const stats = {
          totalRows,
          invalidRows,
          validRows,
          uniqueSkus,
          productsLoaded: existingProducts.length,
          matchedSkus: foundSkus.size,
          candidatesCount: candidates.length,
          notFoundSkusCount: notFoundSkus.length,
        };

        setSnack({
          open: true,
          severity: "info",
          message: `Planilha analisada: ${fmtInt(uniqueSkus)} SKUs. Vou atualizar ${fmtInt(
            willUpdate
          )} preços.`,
        });

        setPriceConfirm({
          fileName: file.name,
          stats,
          candidates,
          notFoundSkus,
        });
      } catch (err) {
        console.error("[price-update] erro:", err);
        setSnack({
          open: true,
          severity: "error",
          message: err?.message || "Falha ao processar planilha de preço.",
        });
      } finally {
        setPriceAnalyzing(false);
        setPriceAnalyzeStep("");
      }
    };

    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  const applyPriceUpdate = async () => {
    if (!priceConfirm?.candidates?.length) return;

    setPriceApplying(true);

    const firstProjectIdLocal = firstProjectId ? String(firstProjectId) : null;

    const items = priceConfirm.candidates.filter(
      (it) => it.currentPrice !== it.sheetPrice
    );

    const settled = await asyncPool(10, items, async (it) => {
      return updateMutation.mutateAsync({
        id: it.id,
        preco: it.sheetPrice,
        id_usuario_ultima_alteracao: userData?.id ?? null,
        ...(firstProjectIdLocal ? { id_projeto: Number(firstProjectIdLocal) } : {}),
      });
    });

    const ok = settled.filter((r) => r.status === "fulfilled").length;
    const fail = settled.length - ok;

    setPriceApplying(false);
    setPriceConfirm(null);
    await refetch();

    setSnack({
      open: true,
      severity: fail ? "warning" : "success",
      message: fail
        ? `Preços: ${fmtInt(ok)} atualizados, ${fmtInt(fail)} falharam.`
        : `Preços atualizados: ${fmtInt(ok)} produtos.`,
    });
  };

  const importCadastroCentral = async () => {
    if (centralLoading) return;

    setCentralLoading(true);
    setCentralOpen(true);
    setCentralProgressDetail(tProducts("centralImport.starting"));

    const workspaceKey = getActiveWorkspaceSlug() || "workspace";
    const resumeStorageKey = `xproCentralSyncCursor:${workspaceKey}:${idsProjetos || "all"}`;

    const readResumeCursor = () => {
      try {
        const raw = sessionStorage.getItem(resumeStorageKey);
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : 0;
      } catch {
        return 0;
      }
    };
    const writeResumeCursor = (value) => {
      try {
        if (value > 0) sessionStorage.setItem(resumeStorageKey, String(value));
        else sessionStorage.removeItem(resumeStorageKey);
      } catch {
        /* ignore */
      }
    };

    try {
      const qs = idsProjetos ? `?ids_projetos=${encodeURIComponent(idsProjetos)}` : "";
      const totals = {
        processed: 0,
        created: 0,
        updated: 0,
        errors: 0,
        ignored: 0,
        released_soft_deleted: 0,
        skipped_no_key: 0,
        skipped_uptodate: 0,
      };

      // Retoma de onde parou (timeout / corte AWS). Sem isso, cada clique
      // reprocessa o início e nunca chega nos SKUs finais (ex.: 1889543).
      let cursor = readResumeCursor();
      let hasMore = true;
      let pages = 0;
      let incomplete = false;
      // Lotes menores: API Gateway/Lambda `app` ~29s em AWS.
      const PAGE_SIZE = 25;
      const MAX_PAGES = 200;
      const REQUEST_TIMEOUT_MS = 280000;

      if (cursor > 0) {
        setCentralProgressDetail(
          tProducts("centralImport.resuming", { cursor: fmtInt(cursor) })
        );
      }

      while (hasMore && pages < MAX_PAGES) {
        pages += 1;
        setCentralProgressDetail(
          tProducts("centralImport.progressBatch", {
            page: pages,
            processed: fmtInt(totals.processed),
            created: fmtInt(totals.created),
            updated: fmtInt(totals.updated),
          })
        );

        const res = await fetchData(
          `/products/import-central${qs}`,
          "POST",
          {
            centralCursor: cursor,
            pageSize: PAGE_SIZE,
          },
          { timeout: REQUEST_TIMEOUT_MS }
        );
        const r = res?.data || res || {};

        totals.processed += Number(r.processed) || 0;
        totals.created += Number(r.created) || 0;
        totals.updated += Number(r.updated) || 0;
        totals.errors += Number(r.errors) || 0;
        totals.ignored += Number(r.ignored) || 0;
        totals.released_soft_deleted += Number(r.released_soft_deleted) || 0;
        totals.skipped_no_key += Number(r.skipped_no_key) || 0;
        totals.skipped_uptodate += Number(r.skipped_uptodate) || 0;

        hasMore = Boolean(r.hasMore);
        const next = Number(r.nextCursor);
        if (!Number.isFinite(next) || next <= cursor) {
          hasMore = false;
        } else {
          cursor = next;
          // Persiste progresso a cada lote concluído (permite retomar após falha).
          writeResumeCursor(hasMore ? cursor : 0);
        }
      }

      if (hasMore) {
        incomplete = true;
        writeResumeCursor(cursor);
      } else {
        writeResumeCursor(0);
      }

      const parts = [
        `${fmtInt(totals.processed)} processados`,
        `${fmtInt(totals.created)} novos`,
        `${fmtInt(totals.updated)} atualizados`,
      ];
      if (totals.errors) parts.push(`${fmtInt(totals.errors)} erros`);
      if (totals.ignored) parts.push(`${fmtInt(totals.ignored)} ignorados`);
      if (totals.skipped_uptodate) {
        parts.push(`${fmtInt(totals.skipped_uptodate)} já atualizados`);
      }
      if (totals.released_soft_deleted) {
        parts.push(`${fmtInt(totals.released_soft_deleted)} SKUs liberados (excluídos)`);
      }
      if (totals.skipped_no_key) parts.push(`${fmtInt(totals.skipped_no_key)} sem SKU`);
      if (incomplete) parts.push(tProducts("centralImport.partialHint"));

      setSnack({
        open: true,
        severity: totals.errors || incomplete ? "warning" : "success",
        message: `Cadastro Central: ${parts.join(", ")}.`,
      });
      await refetch();
    } catch (err) {
      // Mantém cursor salvo do último lote OK para o próximo clique continuar.
      const raw = String(err?.message || err?.code || "");
      const timedOut =
        err?.code === "ECONNABORTED" ||
        /timeout|timed?\s*out|504|503/i.test(raw);
      setSnack({
        open: true,
        severity: "error",
        message: timedOut
          ? tProducts("centralImport.timeoutHint")
          : err?.message || "Falha ao importar do cadastro central.",
      });
    } finally {
      setCentralLoading(false);
      setCentralOpen(false);
      setCentralProgressDetail("");
    }
  };

  const handleImportPassagensErp = () => {
    if (!firstProjectId) {
      setSnack({
        open: true,
        severity: "error",
        message: tProducts("toolbar.importPassagensErpNeedProject"),
      });
      return;
    }
    setImportPassagensDialogOpen(true);
  };

  const syncLinkedWorkspacesFromCentral = async () => {
    if (bulkCentralLoading) {
      openProgressDialog();
      return;
    }
    if (Array.isArray(bulkCentralSummary) && bulkCentralSummary.length > 0 && centralLinkedJob) {
      openProgressDialog();
      return;
    }

    setLinkedWsSyncMode("full");
    setLinkedWsSyncDialogOpen(true);
  };

  const confirmLinkedWsSync = async () => {
    setLinkedWsSyncDialogOpen(false);
    try {
      await startCentralLinkedImportJobFlow({ syncMode: linkedWsSyncMode });
    } catch (err) {
      setSnack({
        open: true,
        severity: "error",
        message:
          err?.message ||
          err?.response?.data?.message ||
          "Falha ao atualizar workspaces vinculados ao cadastro central.",
      });
    }
  };

  const auditLinkedWorkspacesFromCentral = async () => {
    if (!firstProjectId) {
      setSnack({
        open: true,
        severity: "error",
        message: "Nenhuma empresa/projeto selecionado para auditar workspaces vinculados.",
      });
      return;
    }

    setAuditLinkedLoading(true);
    try {
      const planRes = await fetchData(
        `/products/import-central-linked?ids_projetos=${encodeURIComponent(String(firstProjectId))}`,
        "GET"
      );
      const plan = planRes?.data || planRes || {};
      const items = Array.isArray(plan.items) ? plan.items : [];

      const eligible = items.filter(
        (it) => it.status === "ok" && Array.isArray(it.projects) && it.projects.length > 0
      );

      if (!eligible.length) {
        setSnack({
          open: true,
          severity: "warning",
          message:
            plan?.message ||
            "Nenhum workspace vinculado elegível para auditoria com base nas regiões e configurações atuais.",
        });
        return;
      }

      const diffs = [];

      for (const it of eligible) {
        for (const proj of it.projects) {
          let page = 1;
          let hasMore = true;

          while (hasMore) {
            const resp = await fetchData("/products/central-price-audit-linked", "POST", {
              workspace: it.workspace,
              projectId: proj.id,
              sourceProjectId: firstProjectId,
              page,
              pageSize: 500,
            });
            const payload = resp?.data || resp || {};
            const lot = Array.isArray(payload.items) ? payload.items : [];
            diffs.push(...lot);
            hasMore = Boolean(payload.hasMore);
            page += 1;
          }
        }
      }

      if (!diffs.length) {
        setSnack({
          open: true,
          severity: "success",
          message:
            "Nenhuma divergência de preço encontrada entre cadastro central e workspaces vinculados.",
        });
        return;
      }

      setAuditDialog({ open: true, items: diffs });
    } catch (err) {
      setSnack({
        open: true,
        severity: "error",
        message:
          err?.message ||
          err?.response?.data?.message ||
          "Falha ao auditar preços nos workspaces vinculados.",
      });
    } finally {
      setAuditLinkedLoading(false);
    }
  };

  // substitui a função downloadAndUploadToS3 do front
  const uploadExternalUrlsViaBackend = async (rawUrls, workspaceId) => {
    const urls = dedupeImageUrls(splitImageUrls(rawUrls));
    if (!urls.length) return { galeria: [], failedUrls: [] };

    const prefix = buildPrefix(workspaceId);
    const galeria = [];
    const failedUrls = [];

    for (let i = 0; i < urls.length; i++) {
      const src = urls[i];
      try {
        const name = guessNameFromUrl(src, i);
        const resp = await fetchData("/uploads/fetch", "POST", {
          url: src,
          prefix,
          filename: name,
        });
        const dataResp = resp?.data || resp;
        if (dataResp?.url) {
          galeria.push({
            url: dataResp.url,
            ordem: galeria.length + 1,
            isCover: galeria.length === 0,
          });
        } else {
          failedUrls.push(src);
        }
      } catch (e) {
        failedUrls.push(src);
        console.warn("[import] falha ao proxy-fetchar imagem:", src, e?.message || e);
      }
    }
    return { galeria, failedUrls };
  };

  const explainImportSaveError = (err) => {
    const msg = String(err?.message || err?.response?.data?.message || "").trim();
    const pairs = [
      [/sem projeto/i, "importDialog.errNoProject", "importDialog.errNoProjectHint"],
      [/SKU já existente/i, "importDialog.errSkuExists", "importDialog.errSkuExistsHint"],
      [/preco_comdesconto/i, "importDialog.errDiscount", "importDialog.errDiscountHint"],
      [/Nome é obrigatório/i, "importDialog.errName", "importDialog.errNameHint"],
      [/Preço é obrigatório/i, "importDialog.errPrice", "importDialog.errPriceHint"],
      [/Estoque é obrigatório/i, "importDialog.errStock", "importDialog.errStockHint"],
      [/máximo 50|maximo 50/i, "importDialog.errSkuLen", "importDialog.errSkuLenHint"],
      [/SKU inválido/i, "importDialog.errSku", "importDialog.errSkuHint"],
      [/deve ser numérico/i, "importDialog.errNumber", "importDialog.errNumberHint"],
      [/Produto não encontrado/i, "importDialog.errMissing", "importDialog.errMissingHint"],
      [/Network Error|Failed to fetch|timeout|ECONNABORTED/i, "importDialog.errNetwork", "importDialog.errNetworkHint"],
    ];
    const hit = pairs.find(([re]) => re.test(msg));
    if (hit) {
      return { reason: tProducts(hit[1]), hint: tProducts(hit[2]) };
    }
    return {
      reason: msg || tProducts("importDialog.errGeneric"),
      hint: tProducts("importDialog.errGenericHint"),
    };
  };

  const imageMissHint = (failedUrls) => {
    const drive = failedUrls.some((url) => /drive\.google\.com/i.test(String(url)));
    return tProducts(drive ? "importDialog.imageDriveHint" : "importDialog.imageHint", {
      count: failedUrls.length,
    });
  };

  /* ===== Import apply ===== */
  const applyImportRows = async () => {
    if (!importRows.length) return;
    setImportApplying(true);

    const workspaceId = userData?.id_workspace || "default";
    const selectedNow = getSelectedCompanyIds();
    const idProjetoForCreate = selectedNow.length === 1
      ? String(selectedNow[0])
      : firstProjectId
        ? String(firstProjectId)
        : null;

    if (!idProjetoForCreate) {
      setImportApplying(false);
      setSnack({
        open: true,
        severity: "error",
        message: tProducts("toolbar.selectOneCompanyForNewProduct"),
      });
      return;
    }

    const markImportRow = (index, patch) => {
      setImportRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    let ok = 0;
    let fail = 0;

    for (let i = 0; i < importRows.length; i++) {
      const r = importRows[i];
      if (r.__status === "error" || r.__status === "saved") continue;

      markImportRow(i, { __status: "saving", __errors: [], __saveHint: "" });

      try {
        const hasField = (field) => importFilledFields.includes(field);
        const { galeria, failedUrls } = hasField("imagens_urls")
          ? await uploadExternalUrlsViaBackend(r.imagens_urls, workspaceId)
          : { galeria: [], failedUrls: [] };

        const detalhes =
          (r.detalhes && String(r.detalhes).trim())
            ? String(r.detalhes).trim()
            : r.descricao_ia
              ? String(r.descricao_ia).trim()
              : null;

        const compatAplicacoes = [];
        const aplicacaoLabels = new Set();
        const hasSeries = hasField("aplicacao_serie_truck") || hasField("aplicacao_serie_bus");

        if (hasSeries && Array.isArray(r.aplicacao_serie_truck) && r.aplicacao_serie_truck.length) {
          aplicacaoLabels.add("Caminhão");
          r.aplicacao_serie_truck.forEach((fam) => {
            const family = String(fam || "").trim();
            if (family) compatAplicacoes.push({ type: "truck", family });
          });
        }
        if (hasSeries && Array.isArray(r.aplicacao_serie_bus) && r.aplicacao_serie_bus.length) {
          aplicacaoLabels.add("Ônibus");
          r.aplicacao_serie_bus.forEach((fam) => {
            const family = String(fam || "").trim();
            if (family) compatAplicacoes.push({ type: "bus", family });
          });
        }

        const effectiveId = r.id || r.__existingProductId || null;
        const isUpdate = Boolean(effectiveId);

        const base = {
          id: r.id,
          sku: (r.sku ?? "").toString().trim() || null,
          nome: r.nome,
          id_usuario_ultima_alteracao: userData?.id ?? null,
          id_projeto: Number(idProjetoForCreate),
          ids_projetos: idProjetoForCreate,
        };

        if (!isUpdate || hasField("preco")) base.preco = n(r.preco);
        if (!isUpdate || hasField("tipo")) base.tipo = n(r.tipo) || 1;
        if (!isUpdate || hasField("estoque")) base.estoque = n(r.estoque) || 0;
        if (!isUpdate || hasField("status")) base.status = brToBool(r.status);
        if (!isUpdate || hasField("preco_comdesconto")) base.preco_comdesconto = n(r.preco_comdesconto);
        if (hasField("detalhes") || hasField("descricao_ia")) base.detalhes = detalhes ?? null;
        if (hasField("id_categoria") || hasField("categoria_nome")) {
          base.id_categoria = r.id_categoria ?? null;
        }
        if (hasField("id_subcategoria") || hasField("subcategoria_nome")) {
          base.id_subcategoria = r.id_subcategoria ?? null;
        }
        if (hasField("peso")) base.peso = n(r.peso);
        if (hasField("largura")) base.largura = n(r.largura);
        if (hasField("altura")) base.altura = n(r.altura);
        if (hasField("comprimento")) base.comprimento = n(r.comprimento);
        if (hasField("imagens_urls")) {
          base.galeria = galeria;
          const cover = galeria.find((g) => g.isCover) || galeria[0];
          if (cover?.url) base.imagem = cover.url;
        }
        if (hasField("inclusos_itens")) {
          base.inclusos = {
            itens: Array.isArray(r.inclusos_itens)
              ? r.inclusos_itens.map((x) => String(x || "").trim()).filter(Boolean)
              : [],
          };
        }
        if (hasSeries) {
          base.aplicacao = Array.from(aplicacaoLabels);
          base.compatibilidade = { aplicacoes: compatAplicacoes };
        }

        if (isUpdate) {
          await updateMutation.mutateAsync({ ...base, id: effectiveId });
        } else {
          await createMutation?.mutateAsync({
            ...base,
            id: undefined,
            id_usuario: userData?.id ?? null,
          });
        }

        ok += 1;
        markImportRow(i, {
          __status: "saved",
          __errors: [],
          __saveHint: failedUrls.length ? imageMissHint(failedUrls) : "",
        });
      } catch (err) {
        fail += 1;
        const explained = explainImportSaveError(err);
        markImportRow(i, {
          __status: "failed",
          __errors: [explained.reason],
          __saveHint: explained.hint,
        });
      }
    }

    setImportApplying(false);
    await refetch();

    setSnack({
      open: true,
      severity: fail ? "warning" : "success",
      message: fail
        ? tProducts("importDialog.resultPartial", { ok, fail })
        : tProducts("importDialog.resultOk", { ok }),
    });
  };

  /**
   * Importa vínculos de produtos relacionados (cross-sell manual) via planilha.
   * Espera colunas SKU_Principal e SKU_Relacionado (case-insensitive/acentos ignorados).
   */
  const handlePickRelatedImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (evt) => {
      try {
        const binary = evt.target.result;
        const wb = XLSX.read(binary, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

        if (!rows.length) {
          setSnack({
            open: true,
            severity: "error",
            message: "Planilha vazia. Preencha pelo menos uma linha com SKUs.",
          });
          return;
        }

        const items = [];
        let invalid = 0;

        for (const r of rows) {
          const principalRaw = pickCol(r, [
            "sku_principal",
            "SKU_Principal",
            "sku principal",
            "principal",
            "sku_filtro",
            "sku filtro",
          ]);
          const relatedRaw = pickCol(r, [
            "sku_relacionado",
            "SKU_Relacionado",
            "sku relacionado",
            "relacionado",
            "sku_peca",
            "sku peça",
          ]);

          const principal = (principalRaw ?? "").toString().trim();
          const related = (relatedRaw ?? "").toString().trim();

          if (!principal || !related || principal === related) {
            invalid++;
            continue;
          }

          items.push({ principalSku: principal, relatedSku: related });
        }

        if (!items.length) {
          setSnack({
            open: true,
            severity: "error",
            message:
              "Nenhuma linha válida encontrada. Certifique-se de ter colunas SKU_Principal e SKU_Relacionado.",
          });
          return;
        }

        const body = { items };
        if (idsProjetos) {
          body.ids_projetos = idsProjetos;
        }

        const res = await fetchData("/products/related-import", "POST", body);
        const r = res?.data || res || {};

        const msg = `Relacionados: ${
          r.distinctPrincipals || 0
        } produtos atualizados, ${fmtInt(
          r.processedRows || items.length
        )} linhas processadas${
          invalid ? `, ${fmtInt(invalid)} ignoradas por problemas de preenchimento.` : "."
        }`;

        setSnack({ open: true, severity: "success", message: msg });
      } catch (err) {
        console.error("[related-import] erro:", err);
        setSnack({
          open: true,
          severity: "error",
          message:
            err?.message ||
            "Falha ao importar relacionados. Verifique o arquivo e tente novamente.",
        });
      } finally {
        e.target.value = null;
      }
    };

    reader.readAsBinaryString(file);
  };

  /** aplica atualização de estoque com ajuste (subtrair) */
  const applyStockUpdate = async () => {
    if (!stockComputed?.toUpdate?.length) return;

    const adj = Number(stockAdjustment);
    const safeAdj = Number.isFinite(adj) ? Math.max(0, Math.floor(adj)) : 0;

    setStockApplying(true);

    const firstProjectIdLocal = firstProjectId ? String(firstProjectId) : null;
    const items = stockComputed.toUpdate;

    const settled = await asyncPool(10, items, async (it) => {
      return updateMutation.mutateAsync({
        id: it.id,
        estoque: it.finalStock,
        id_usuario_ultima_alteracao: userData?.id ?? null,
        ...(firstProjectIdLocal ? { id_projeto: Number(firstProjectIdLocal) } : {}),
      });
    });

    const ok = settled.filter((r) => r.status === "fulfilled").length;
    const fail = settled.length - ok;

    const notFound = stockConfirm?.notFoundSkus?.length || 0;
    const invalid = stockConfirm?.stats?.invalidRows || 0;

    setStockApplying(false);
    setStockConfirm(null);
    await refetch();

    setSnack({
      open: true,
      severity: fail ? "warning" : "success",
      message: fail
        ? `Estoque: ${fmtInt(ok)} atualizados, ${fmtInt(fail)} falharam. (SKUs não encontrados: ${fmtInt(
            notFound
          )} | linhas inválidas: ${fmtInt(invalid)} | ajuste: -${fmtInt(safeAdj)})`
        : `Estoque atualizado: ${fmtInt(ok)} produtos. (SKUs não encontrados: ${fmtInt(
            notFound
          )} | linhas inválidas: ${fmtInt(invalid)} | ajuste: -${fmtInt(safeAdj)})`,
    });
  };

  const exportTemplate = () => {
    const blank = {
      id: "",
      SKU: "",
      Nome: "",
      Tipo: "",
      Preço: "",
      "Preço com desconto": "",
      Estoque: "",
      Status: "",
      Descrição: "",
      Categoria: "",
      Subcategoria: "",
      "ID da categoria": "",
      "ID da subcategoria": "",
      Peso: "",
      Largura: "",
      Altura: "",
      Comprimento: "",
      "Série caminhão": "",
      "Série ônibus": "",
      Imagens: "",
      "Itens inclusos": "",
    };
    const sample = [
      {
        ...blank,
        SKU: "FILTRO-001",
        Nome: "Filtro de óleo",
        Tipo: 1,
        Preço: 89.9,
        "Preço com desconto": 79.9,
        Estoque: 10,
        Status: 1,
        Descrição: "Filtro de óleo para linha pesada",
        Categoria: "Filtros",
        Subcategoria: "Óleo",
        Peso: 0.8,
        Largura: 12,
        Altura: 12,
        Comprimento: 15,
        "Série caminhão": "PGR, NTG",
        Imagens:
          "https://drive.google.com/file/d/COLE_O_ID_DO_ARQUIVO/view; https://exemplo.com/foto.jpg",
      },
      {
        ...blank,
        SKU: "SERVICO-010",
        Nome: "Troca de pastilha",
        Tipo: 2,
        Preço: 210,
        Estoque: 4,
        Status: 0,
        Categoria: "Freios",
        "Série ônibus": "K-Series",
      },
      {
        ...blank,
        SKU: "KIT-FREIO-01",
        Nome: "Kit pastilha e disco",
        Tipo: 1,
        Preço: 490,
        Estoque: 3,
        Status: 1,
        Categoria: "Freios",
        "Itens inclusos": "Pastilha dianteira PAST-010; Disco de freio DISC-020",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sample);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "produtos");
    XLSX.writeFile(wb, "planilha_exemplo_produtos.xlsx");
  };

  const handleRetryLinkedWorkspace = async (workspace, projectId) => {
    if (!firstProjectId) {
      setSnack({
        open: true,
        severity: "error",
        message: "Nenhuma empresa/projeto selecionado para atualizar workspaces vinculados.",
      });
      return;
    }

    const snapshot = (bulkCentralSummary || []).find(
      (row) => row.workspace === workspace && row.projectId === projectId
    );
    if (!snapshot) return;

    let cursor = snapshot.centralCursor || 0;
    let hasMore = true;

    while (hasMore) {
      // marca item como "running"
      setBulkCentralSummary((prev) =>
        (prev || []).map((row) =>
          row.workspace === workspace && row.projectId === projectId
            ? { ...row, status: "running", errorMessage: null }
            : row
        )
      );

      try {
        const resp = await fetchData(
          `/products/import-central-linked?ids_projetos=${encodeURIComponent(String(firstProjectId))}`,
          "POST",
          {
            workspace,
            projectId,
            sourceProjectId: firstProjectId,
            centralCursor: cursor,
            pageSize: 200,
          }
        );
        const payload = resp?.data || resp || {};

        cursor = Number(payload.nextCursor || cursor) || cursor;
        hasMore = Boolean(payload.hasMore);

        setBulkCentralSummary((prev) =>
          (prev || []).map((row) =>
            row.workspace === workspace && row.projectId === projectId
              ? {
                  ...row,
                  status: hasMore ? "running" : "success",
                  processed: payload.processed ?? row.processed ?? null,
                  created: payload.created ?? row.created ?? null,
                  updated: payload.updated ?? row.updated ?? null,
                  centralCursor: cursor,
                  errorMessage: null,
                }
              : row
          )
        );
      } catch (err) {
        const msg =
          err?.message ||
          err?.response?.data?.message ||
          "Falha ao atualizar este workspace/projeto vinculado.";
        setBulkCentralSummary((prev) =>
          (prev || []).map((row) =>
            row.workspace === workspace && row.projectId === projectId
              ? { ...row, status: "error", errorMessage: msg }
              : row
          )
        );
        hasMore = false;
      }
    }
  };

  /* ===== Paste handlers ===== */
  const refreshPastePreview = () => setPastePreview(parsePasteBlock(pasteText));
  const applyPasteRows = async () => {
    const { rowsValid } = pastePreview;
    if (!rowsValid.length) return;

    if (!createMutation?.mutateAsync) {
      setSnack({
        open: true,
        severity: "error",
        message: "Endpoint de criação indisponível.",
      });
      return;
    }

    const idProjetoForCreate = firstProjectId ? String(firstProjectId) : null;

    setPasteApplying(true);
    const ops = rowsValid.map((p) =>
      createMutation.mutateAsync({
        ...p,
        galeria: (p.imagens || []).map((url, idx) => ({
          url,
          ordem: idx + 1,
          isCover: idx === 0,
        })),
        id_usuario: userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
        ...(idProjetoForCreate ? { id_projeto: Number(idProjetoForCreate) } : {}),
      })
    );

    const settled = await Promise.allSettled(ops);
    const ok = settled.filter((r) => r.status === "fulfilled").length;
    const fail = settled.length - ok;

    setPasteApplying(false);
    setPasteOpen(false);
    setPasteText("");
    setPastePreview({ rowsValid: [], rowsInvalid: [] });
    await refetch();

    setSnack({
      open: true,
      severity: fail ? "warning" : "success",
      message: fail ? `Incluídos: ${ok}. Falharam: ${fail}.` : `Incluídos: ${ok}.`,
    });
  };

  /* ===== CRUD handlers ===== */
  const handleEditProduct = async (p) => {
    if (!canUpdateProduct) return;
    try {
      const full = await getById(p.id);
      setSelectedProduct(full || p);
      setErrorMessage(null);
    } catch {
      setSelectedProduct(p);
    }
  };

  const handleDeleteProduct = (id) => {
    if (!canDeleteProduct) return;
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteProduct = () => {
    if (!confirmDelete) return;
    setIsProcessingDelete(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: async () => {
        setConfirmDelete(null);
        await refetch();
      },
      onError: (err) => {
        setErrorMessage(
          err?.message || err?.response?.data?.message || "Erro ao excluir o produto."
        );
      },
      onSettled: () => setIsProcessingDelete(false),
    });
  };

  const handleBulkDeleteSelected = async (ids) => {
    if (!canDeleteProduct) return;
    if (!ids || ids.length === 0) return;
    const settled = await Promise.allSettled(ids.map((id) => deleteMutation.mutateAsync(id)));
    const ok = settled.filter((r) => r.status === "fulfilled").length;
    const fail = settled.length - ok;

    setSnack({
      open: true,
      severity: fail ? "warning" : "success",
      message: fail
        ? `Exclusão em massa: ${ok} removidos, ${fail} falharam.`
        : `Exclusão em massa concluída: ${ok} removidos.`,
    });

    await refetch();
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);

  const handleProductSortModelChange = (model) => {
    setProductSortModel(model);
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    // Busca controlada: atualizar termo e voltar para a 1ª página.
    // Não chamamos refetch aqui porque o react-query já refaz a query
    // automaticamente quando o `search` (parte da queryKey) muda.
    setSearch(term);
    setCurrentPage(1);
  };

  const handleExportExcel = async () => {
    try {
      const selectedNow = getSelectedCompanyIds();
      const idsForExport = selectedNow.length ? selectedNow.join(",") : null;
      const includeIdProjeto = selectedNow.length > 1;

      setSnack({
        open: true,
        severity: "info",
        message: "Preparando exportação de produtos...",
      });

      const result = await exportProductsSpreadsheet(
        {
          search: search || undefined,
          ids_projetos: idsForExport || undefined,
          include_id_projeto: includeIdProjeto ? 1 : 0,
        },
        {
          onStatus: (_status, job) => {
            const processed = job?.processed ?? 0;
            const total = job?.total;
            const pct =
              job?.overallProgress != null && Number.isFinite(Number(job.overallProgress))
                ? Math.round(Number(job.overallProgress))
                : null;
            const progressLabel =
              total != null
                ? `${processed}/${total}${pct != null ? ` (${pct}%)` : ""}`
                : `${processed} processados`;
            setSnack({
              open: true,
              severity: "info",
              message: `Exportando produtos... ${progressLabel}`,
            });
          },
        }
      );

      if (!result?.downloadUrl) {
        setSnack({
          open: true,
          severity: "warning",
          message: "Nenhum produto para exportar.",
        });
        return;
      }

      const a = document.createElement("a");
      a.href = result.downloadUrl;
      a.download = "produtos.xlsx";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();

      setSnack({
        open: true,
        severity: "success",
        message:
          result.processed != null
            ? `Exportação concluída (${result.processed} produtos).`
            : "Exportação concluída.",
      });
    } catch (err) {
      console.error("Erro ao exportar produtos:", err);
      setSnack({
        open: true,
        severity: "error",
        message: err?.message || "Falha ao exportar produtos.",
      });
    }
  };

  return (
    <Box className="tour-products-root" sx={{ paddingTop: 1 }}>
      <Box
        className="tour-products-header"
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 1,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {tSettings(productsPageTitleI18nKey(segment))}
        </Typography>
        <ProductsToolbar
          onOpenImportGuide={() => setImportGuideOpen(true)}
          onPickStockFile={handlePickStockUpdate}
          canUpdateStock={canUpdateProduct && !stockAnalyzing}
          onImportCentral={importCadastroCentral}
          importCentralLoading={centralLoading}
          onBulkCentralSync={syncLinkedWorkspacesFromCentral}
          bulkCentralLoading={bulkCentralLoading || linkedWorkspacesChecking}
          bulkCentralJobActive={Boolean(bulkCentralLoading)}
          hasLinkedWorkspaces={hasLinkedWorkspaces}
          onAuditLinked={auditLinkedWorkspacesFromCentral}
          auditLinkedLoading={auditLinkedLoading}
          onOpenPaste={() => setPasteOpen(true)}
          showImportPassagensErp={showImportPassagensErp}
          onImportPassagensErp={handleImportPassagensErp}
          isPassengerTransport={isPassengerTransport}
          onCreateNew={
            canCreateProduct
              ? () =>
                  setSelectedProduct(
                    isPassengerTransport ? { tipo: 5, estoque: 0, preco: 0, status: 1 } : {}
                  )
              : undefined
          }
          onPickPriceFile={handlePickPriceUpdate}
          canUpdatePrice={canUpdateProduct && !priceAnalyzing}
          onPickRelatedFile={handlePickRelatedImport}
          onExportExcel={handleExportExcel}
          onReviewImages={() => setImageAuditOpen(true)}
        />
      </Box>

      <ImportPassagensErpDialog
        open={importPassagensDialogOpen}
        onClose={() => setImportPassagensDialogOpen(false)}
        projectId={firstProjectId}
        onCompleted={refetch}
      />

      <ProductImageAuditDialog
        open={imageAuditOpen}
        onClose={() => setImageAuditOpen(false)}
        idsProjetos={idsProjetos || ""}
      />

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedProduct ? (
        isPassengerTransport ? (
          <AddTrechoForm
            isFetching={isFetching}
            product={selectedProduct}
            defaultProjectId={firstProjectId}
            onClose={() => {
              setSelectedProduct(null);
              setErrorMessage(null);
            }}
            onSubmit={(updatedProduct) => {
              const firstProjectIdLocal = firstProjectId ? String(firstProjectId) : null;
              const base = {
                ...updatedProduct,
                id_usuario: updatedProduct.id
                  ? updatedProduct.id_usuario ?? userData?.id
                  : updatedProduct.id_usuario ?? userData?.id,
                id_usuario_ultima_alteracao: userData?.id ?? null,
                ...(firstProjectIdLocal ? { id_projeto: Number(firstProjectIdLocal) } : {}),
              };
              const isUpdate = Boolean(base.id);
              if (isUpdate && !canUpdateProduct) {
                const msg = "Você não tem permissão para editar produtos.";
                setErrorMessage(msg);
                setSnack({ open: true, severity: "error", message: msg });
                return Promise.reject(new Error(msg));
              }
              if (!isUpdate && !canCreateProduct) {
                const msg = "Você não tem permissão para criar produtos.";
                setErrorMessage(msg);
                setSnack({ open: true, severity: "error", message: msg });
                return Promise.reject(new Error(msg));
              }
              const mutate = base.id
                ? updateMutation.mutateAsync
                : createMutation?.mutateAsync || updateMutation.mutateAsync;
              return mutate(base)
                .then(() => {
                  setSelectedProduct(null);
                  setSnack({
                    open: true,
                    severity: "success",
                    message: isUpdate ? "Trecho atualizado com sucesso." : "Trecho cadastrado com sucesso.",
                  });
                  return refetch();
                })
                .catch((err) => {
                  const msg =
                    err?.message ||
                    err?.response?.data?.message ||
                    "Erro ao salvar o trecho. Tente novamente.";
                  setErrorMessage(msg);
                  setSnack({ open: true, severity: "error", message: msg });
                  throw err;
                });
            }}
          />
        ) : (
          <AddProductForm
          isFetching={isFetching}
          product={selectedProduct}
          defaultProjectId={firstProjectId}
          onClose={() => {
            setSelectedProduct(null);
            setErrorMessage(null);
          }}
          onSubmit={(updatedProduct) => {
            const firstProjectIdLocal = firstProjectId ? String(firstProjectId) : null;

            const base = {
              ...updatedProduct,
              id_usuario: updatedProduct.id
                ? updatedProduct.id_usuario ?? userData?.id
                : updatedProduct.id_usuario ?? userData?.id,
              id_usuario_ultima_alteracao: userData?.id ?? null,
              ...(firstProjectIdLocal ? { id_projeto: Number(firstProjectIdLocal) } : {}),
            };

            const isUpdate = Boolean(base.id);
            if (isUpdate && !canUpdateProduct) {
              const msg = "Você não tem permissão para editar produtos.";
              setErrorMessage(msg);
              setSnack({ open: true, severity: "error", message: msg });
              return Promise.reject(new Error(msg));
            }
            if (!isUpdate && !canCreateProduct) {
              const msg = "Você não tem permissão para criar produtos.";
              setErrorMessage(msg);
              setSnack({ open: true, severity: "error", message: msg });
              return Promise.reject(new Error(msg));
            }

            const mutate = base.id
              ? updateMutation.mutateAsync
              : createMutation?.mutateAsync || updateMutation.mutateAsync;

            return mutate(base)
              .then(async () => {
                await refetch();
                setSelectedProduct(null);
                setSnack({
                  open: true,
                  severity: "success",
                  message: base.id ? "Produto atualizado." : "Produto criado.",
                });
              })
              .catch((err) => {
                const msg =
                  err?.message || err?.response?.data?.message || "Erro ao salvar o produto.";
                setErrorMessage(msg);
                setSnack({ open: true, severity: "error", message: msg });
              });
          }}
        />
        )
      ) : (
        <Box className="tour-products-table">
        <ProductTable
          products={data?.products || []}
          isPassengerTransport={isPassengerTransport}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={setPageSize}
          sortingMode="server"
          sortModel={productSortModel}
          onSortModelChange={handleProductSortModelChange}
          onSearch={handleSearch}
          searchValue={search}
          onSearchChange={handleSearch}
          isLoading={isLoading || isFetching}
          // Editar só se tiver permissão de update
          onEditProduct={canUpdateProduct ? handleEditProduct : undefined}
          // Excluir só se tiver permissão de delete
          onDeleteProduct={canDeleteProduct ? handleDeleteProduct : undefined}
          onBulkUpdate={async (payload) => {
            // Bulk edit (preço/estoque/status) não deve alterar vínculo de projeto.
            // Alguns fluxos carregam `ids_projetos` do contexto (ex.: múltiplas empresas selecionadas),
            // e o backend rejeita update com mais de 1 projeto. Então removemos qualquer campo de projeto aqui.
            const cleaned = { ...(payload || {}) };
            if ("ids_projetos" in cleaned) delete cleaned.ids_projetos;
            if ("id_projeto" in cleaned) delete cleaned.id_projeto;

            await updateMutation.mutateAsync({
              ...cleaned,
              id_usuario_ultima_alteracao: userData?.id ?? null,
            });
          }}
          onReload={async () => {
            await refetch();
          }}
          currentUserId={userData?.id ?? null}
          // Exclusão em massa só se tiver delete
          onDeleteSelected={canDeleteProduct ? handleBulkDeleteSelected : undefined}
        />
        </Box>
      )}

      {/* Exclusão */}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Confirmação de Exclusão"
        content={
          isProcessingDelete ? "Processando exclusão..." : "Tem certeza de que deseja excluir este produto?"
        }
        loading={isProcessingDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={confirmDeleteProduct}
        confirmText="Excluir"
        confirmColor="error"
      />

      {/* Loading enquanto analisa planilha (UI melhorada) */}
      <Backdrop
        open={stockAnalyzing}
        sx={{
          zIndex: (t) => t.zIndex.modal + 1,
          backgroundColor: "rgba(10, 10, 12, 0.55)",
          backdropFilter: "blur(6px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "min(560px, calc(100vw - 32px))",
            borderRadius: 3,
            p: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                backgroundColor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
                flex: "0 0 auto",
              }}
            >
              <Inventory2RoundedIcon fontSize="small" />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Analisando planilha para atualizar
              </Typography>

              <Typography
                variant="body2"
                sx={{ mt: 0.75, color: "text.secondary" }}
                noWrap
              >
                {stockAnalyzeStep || "Processando…"}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <LinearProgress />
              </Box>

              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  color: "text.secondary",
                }}
              >
                <CircularProgress size={18} />
                <Typography variant="caption">
                  Isso pode levar alguns segundos dependendo do tamanho da planilha.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Backdrop>

      {/* Loading enquanto audita preços nos workspaces vinculados */}
      <Backdrop
        open={auditLinkedLoading}
        sx={{
          zIndex: (t) => t.zIndex.modal + 1,
          backgroundColor: "rgba(10, 10, 12, 0.55)",
          backdropFilter: "blur(6px)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "min(520px, calc(100vw - 32px))",
            borderRadius: 3,
            p: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                backgroundColor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <CircularProgress size={26} thickness={4} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Auditando preços nos workspaces vinculados
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Estamos comparando os preços do cadastro central com cada empresa vinculada.
                Este processo pode levar alguns instantes dependendo do volume de produtos.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Backdrop>

      <ImportGuideDialog
        open={importGuideOpen}
        reading={importReading}
        onClose={() => setImportGuideOpen(false)}
        onDownloadTemplate={exportTemplate}
        onPickFile={handlePickImport}
      />

      {/* Import planilha */}
      <ImportDialog
        open={importOpen}
        rows={importRows}
        applying={importApplying}
        remapping={importRemapping}
        filledFields={importFilledFields}
        ambiguousFields={importAmbiguous}
        columnChoices={importChoices}
        onChangeColumnChoice={handleImportColumnChoice}
        onClose={closeImportPreview}
        onApply={applyImportRows}
      />

      {/* Confirmação Atualizar Estoque (com ajuste) */}
      <ConfirmDialog
        open={!!stockConfirm}
        title="Confirmação de Atualização de Estoque"
        content={
          stockConfirm ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Typography variant="body2">
                <strong>Arquivo:</strong> {stockConfirm.fileName}
              </Typography>

              <Divider />

              <Typography variant="body2">
                <strong>O que será feito:</strong>
              </Typography>
              <Typography variant="body2">
                • Atualizar <strong>{fmtInt(stockComputed.toUpdate.length)}</strong> produtos pelo SKU.
              </Typography>
              <Typography variant="body2">
                • Regra aplicada: <strong>estoque_final = max(0, estoque_planilha - ajuste)</strong>.
              </Typography>

              <Box sx={{ mt: 0.5 }}>
                <TextField
                  label="Ajuste (subtrair do estoque da planilha)"
                  type="number"
                  size="small"
                  value={stockAdjustment}
                  onChange={(ev) => {
                    const v = Number(ev.target.value);
                    if (!Number.isFinite(v)) return setStockAdjustment(0);
                    setStockAdjustment(Math.max(0, Math.floor(v)));
                  }}
                  inputProps={{ min: 0, step: 1 }}
                  helperText="Padrão: 2 (ex.: planilha 10 → grava 8). Nunca fica negativo."
                  fullWidth
                  disabled={stockApplying}
                />
              </Box>

              <Divider />

              <Typography variant="body2">
                <strong>Resumo da análise:</strong>
              </Typography>
              <Typography variant="body2">
                • Linhas na planilha: {fmtInt(stockConfirm.stats?.totalRows)} (válidas:{" "}
                {fmtInt(stockConfirm.stats?.validRows)} | inválidas: {fmtInt(stockConfirm.stats?.invalidRows)})
              </Typography>
              <Typography variant="body2">
                • SKUs únicos válidos: {fmtInt(stockConfirm.stats?.uniqueSkus)}
              </Typography>
              <Typography variant="body2">
                • SKUs encontrados no cadastro: {fmtInt(stockConfirm.stats?.matchedSkus)} | não encontrados:{" "}
                {fmtInt(stockConfirm.stats?.notFoundSkusCount)}
              </Typography>
              <Typography variant="body2">
                • Itens ignorados (já iguais ao estoque da planilha): {fmtInt(stockComputed.unchangedSkipped)}
              </Typography>
              <Typography variant="body2">
                • SKUs encontrados no cadastro: {fmtInt(stockConfirm.stats?.matchedSkus)} (candidatos: {fmtInt(stockConfirm.candidates?.length)})
              </Typography>

              {stockConfirm.notFoundSkus?.length ? (
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Dica: há SKUs na planilha que não existem no cadastro. (Exibindo apenas no log se precisar.)
                </Typography>
              ) : null}
            </Box>
          ) : (
            ""
          )
        }
        loading={stockApplying}
        onClose={() => (stockApplying ? null : setStockConfirm(null))}
        onConfirm={applyStockUpdate}
        confirmText={stockApplying ? "Aplicando..." : "Aplicar"}
        confirmColor="primary"
      />

      <ConfirmDialog
        open={!!priceConfirm}
        title="Confirmação de Atualização de Preços"
        content={
          priceConfirm ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              <Typography variant="body2">
                <strong>Arquivo:</strong> {priceConfirm.fileName}
              </Typography>

              <Divider />

              <Typography variant="body2">
                • Produtos que terão o preço alterado:{" "}
                <strong>
                  {fmtInt(
                    priceConfirm.candidates.filter(
                      (c) => c.currentPrice !== c.sheetPrice
                    ).length
                  )}
                </strong>
              </Typography>

              <Divider />

              <Typography variant="body2">
                • SKUs encontrados: {fmtInt(priceConfirm.stats?.matchedSkus)}
              </Typography>
              <Typography variant="body2">
                • SKUs não encontrados: {fmtInt(priceConfirm.stats?.notFoundSkusCount)}
              </Typography>
            </Box>
          ) : (
            ""
          )
        }
        loading={priceApplying}
        onClose={() => (priceApplying ? null : setPriceConfirm(null))}
        onConfirm={applyPriceUpdate}
        confirmText={priceApplying ? "Aplicando..." : "Aplicar"}
        confirmColor="primary"
      />

      {/* Colar lista */}
      <PasteDialog
        open={pasteOpen}
        text={pasteText}
        setText={setPasteText}
        preview={pastePreview}
        onPreview={refreshPastePreview}
        onApply={applyPasteRows}
        applying={pasteApplying}
        onClose={() => setPasteOpen(false)}
      />

      {/* Cadastro Central */}
      <CentralImportDialog open={centralOpen} detail={centralProgressDetail} />

      {/* Auditoria de preços – diálogo com divergências */}
      <ConfirmDialog
        open={auditDialog.open}
        title="Divergências de preço encontradas"
        fullWidth
        maxWidth="lg"
        content={
          auditDialog.open && Array.isArray(auditDialog.items) && auditDialog.items.length ? (
            <Box sx={{ maxHeight: 500, overflow: "auto", pt: 0.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Workspace</TableCell>
                    <TableCell>Empresa</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Produto</TableCell>
                    <TableCell align="right">Preço workspace</TableCell>
                    <TableCell align="right">Preço central</TableCell>
                    <TableCell>Últ. atual. workspace</TableCell>
                    <TableCell>Últ. atual. central</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditDialog.items.map((it, idx) => (
                    <TableRow
                      key={`${it.workspace}-${it.projectId}-${it.sku}-${idx}`}
                      sx={{
                        "&:nth-of-type(odd)": {
                          bgcolor: "rgba(255, 193, 7, 0.04)",
                        },
                      }}
                    >
                      <TableCell>{it.workspace}</TableCell>
                      <TableCell>
                        {it.projectName ? `${it.projectName} (ID ${it.projectId})` : `ID ${it.projectId}`}
                      </TableCell>
                      <TableCell>{it.sku}</TableCell>
                      <TableCell>{it.productName || "Nome não informado"}</TableCell>
                      <TableCell align="right">
                        {Number(it.linkedPrice).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </TableCell>
                      <TableCell align="right">
                        {Number(it.centralPrice).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </TableCell>
                      <TableCell>{fmtDateTimeBr(it.linkedUpdatedAt)}</TableCell>
                      <TableCell>{fmtDateTimeBr(it.centralUpdatedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            "Nenhuma divergência para exibir."
          )
        }
        loading={false}
        onClose={() => setAuditDialog({ open: false, items: [] })}
        onConfirm={() => setAuditDialog({ open: false, items: [] })}
        confirmText="Fechar"
      />

      {/* Escolha do modo de sincronização antes de iniciar */}
      <ConfirmDialog
        open={linkedWsSyncDialogOpen}
        title={tProducts("linkedWsDialog.syncModeTitle")}
        maxWidth="sm"
        fullWidth
        content={
          <Box sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tProducts("linkedWsDialog.syncModeIntro")}
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={linkedWsSyncMode}
                onChange={(e) => setLinkedWsSyncMode(e.target.value)}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tProducts("linkedWsDialog.syncModeFull")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tProducts("linkedWsDialog.syncModeFullHint")}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mb: 1.5 }}
                />
                <FormControlLabel
                  value="prices_status"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tProducts("linkedWsDialog.syncModePricesStatus")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tProducts("linkedWsDialog.syncModePricesStatusHint")}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start" }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        }
        onClose={() => setLinkedWsSyncDialogOpen(false)}
        onConfirm={confirmLinkedWsSync}
        confirmText={tProducts("linkedWsDialog.syncModeStart")}
        confirmColor="primary"
        cancelText={tProducts("linkedWsDialog.syncModeCancel")}
      />

      {/* Resumo detalhado da atualização de workspaces vinculados */}
      <ConfirmDialog
        open={bulkCentralDialogOpen}
        title={tProducts("linkedWsDialog.title")}
        content={
          <>
            {bulkCentralLoading ? (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {tProducts("linkedWsDialog.backgroundHint")}
              </Alert>
            ) : null}
            {centralLinkedJob?.syncMode ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {centralLinkedJob.syncMode === "prices_status"
                  ? tProducts("linkedWsDialog.syncModeActivePricesStatus")
                  : tProducts("linkedWsDialog.syncModeActiveFull")}
              </Typography>
            ) : null}
            {Array.isArray(bulkCentralSummary) && bulkCentralSummary.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
              {/* Resumo por status */}
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                {(() => {
                  const all = bulkCentralSummary || [];
                  const counts = {
                    success: all.filter((r) => r.status === "success").length,
                    error: all.filter((r) => r.status === "error").length,
                    running: all.filter((r) => r.status === "running").length,
                    pending: all.filter((r) => r.status === "pending").length,
                  };
                  return (
                    <>
                      <Chip
                        size="small"
                        color="success"
                        label={`Atualizados: ${fmtInt(counts.success)}`}
                      />
                      <Chip
                        size="small"
                        color="error"
                        label={`Erros: ${fmtInt(counts.error)}`}
                      />
                      <Chip
                        size="small"
                        color="info"
                        label={`Em andamento: ${fmtInt(counts.running)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Pendentes: ${fmtInt(counts.pending)}`}
                      />
                    </>
                  );
                })()}
              </Stack>

              {bulkCentralLoading || centralLinkedJob?.startedAt ? (
                <Box sx={{ mb: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 0.75 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {tProducts("linkedWsDialog.overallProgress", {
                        pct: fmtInt(bulkCentralOverallProgress ?? 0),
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tProducts("linkedWsDialog.elapsed", {
                        time: formatCentralLinkedDuration(bulkCentralElapsedMs),
                      })}
                      {" · "}
                      {tProducts("linkedWsDialog.eta", {
                        time: formatCentralLinkedDuration(bulkCentralEtaMs),
                      })}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(0, Number(bulkCentralOverallProgress) || 0))}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              ) : null}

              {/* Lista detalhada */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {bulkCentralSummary.map((row, idx) => {
                  const key = `${row.workspace}-${row.projectId}-${idx}`;

                  let statusLabel = "";
                  let statusColor = "default";
                  let IconComponent = ScheduleRoundedIcon;

                  if (row.status === "pending") {
                    statusLabel = "Pendente";
                    statusColor = "default";
                    IconComponent = ScheduleRoundedIcon;
                  } else if (row.status === "running") {
                    statusLabel = "Atualizando…";
                    statusColor = "info";
                    IconComponent = AutorenewRoundedIcon;
                  } else if (row.status === "success") {
                    statusLabel = "Atualizado";
                    statusColor = "success";
                    IconComponent = CheckCircleOutlineRoundedIcon;
                  } else {
                    statusLabel = "Erro";
                    statusColor = "error";
                    IconComponent = ErrorOutlineRoundedIcon;
                  }

                  const bgColor =
                    row.status === "success"
                      ? "rgba(46, 125, 50, 0.12)"
                      : row.status === "error"
                      ? "rgba(211, 47, 47, 0.12)"
                      : row.status === "running"
                      ? "rgba(25, 118, 210, 0.12)"
                      : "action.hover";

                  const borderColor =
                    row.status === "success"
                      ? "success.main"
                      : row.status === "error"
                      ? "error.main"
                      : row.status === "running"
                      ? "info.main"
                      : "divider";

                  return (
                    <Box
                      key={key}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1.5,
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: bgColor,
                        border: "1px solid",
                        borderColor,
                        boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, lineHeight: 1.3 }}
                          noWrap
                        >
                          Workspace: {row.workspace}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.25, lineHeight: 1.35 }}
                          noWrap
                        >
                          Empresa:{" "}
                          {row.projectName
                            ? `${row.projectName} (ID ${row.projectId})`
                            : `ID ${row.projectId}`}
                        </Typography>
                        {row.status === "error" && row.errorMessage && (
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.4,
                              display: "block",
                              color: "error.dark",
                              whiteSpace: "normal",
                            }}
                          >
                            {row.errorMessage}
                          </Typography>
                        )}
                        {row.status === "running" ? (
                          <Box sx={{ mt: 1, maxWidth: 280 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, Math.max(0, Number(row.itemProgress) || 0))}
                              sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {tProducts("linkedWsDialog.itemProgress", {
                                pct: fmtInt(row.itemProgress || 0),
                                synced: fmtInt(row.productsSynced || 0),
                                total: row.productsTotal
                                  ? fmtInt(row.productsTotal)
                                  : "—",
                              })}
                            </Typography>
                          </Box>
                        ) : null}
                        {row.status === "error" && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ mt: 0.75 }}
                            onClick={() => handleRetryLinkedWorkspace(row.workspace, row.projectId)}
                          >
                            Tentar novamente
                          </Button>
                        )}
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        sx={{ flexShrink: 0 }}
                      >
                        <IconComponent
                          fontSize="small"
                          color={
                            statusColor === "success"
                              ? "success"
                              : statusColor === "error"
                              ? "error"
                              : statusColor === "info"
                              ? "info"
                              : "disabled"
                          }
                          sx={
                            row.status === "running"
                              ? { animation: `${spin} 0.9s linear infinite` }
                              : undefined
                          }
                        />
                        <Chip
                          label={statusLabel}
                          size="small"
                          color={statusColor === "default" ? undefined : statusColor}
                          variant={statusColor === "default" ? "outlined" : "filled"}
                        />
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            ) : bulkCentralLoading ? (
              <Box sx={{ py: 2 }}>
                {centralLinkedJob?.cancelRequested ||
                centralLinkedJob?.status === "cancel_requested" ? (
                  <>
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      {tProducts("linkedWsDialog.cancelRequested")}
                    </Alert>
                    <LinearProgress />
                  </>
                ) : (
                  <>
                    <LinearProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      {tProducts("linkedWsDialog.preparing")}
                    </Typography>
                    {(() => {
                      const createdMs = centralLinkedJob?.createdAt
                        ? new Date(centralLinkedJob.createdAt).getTime()
                        : null;
                      const ageSec =
                        Number.isFinite(createdMs) && createdMs > 0
                          ? Math.floor((Date.now() - createdMs) / 1000)
                          : Math.floor(Number(bulkCentralElapsedMs || 0) / 1000);
                      if (ageSec < 90) return null;
                      return (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                          {tProducts("linkedWsDialog.queuedStuckHint")}
                        </Alert>
                      );
                    })()}
                  </>
                )}
              </Box>
            ) : centralLinkedJob?.status === "failed" ? (
              <Alert severity="error">
                {centralLinkedJob?.errorMessage || tProducts("linkedWsDialog.failed")}
              </Alert>
            ) : centralLinkedJob?.status === "cancelled" ? (
              <Alert severity="info">{tProducts("linkedWsDialog.cancelled")}</Alert>
            ) : (
              ""
            )}
          </>
        }
        loading={false}
        onClose={closeProgressDialog}
        onConfirm={closeProgressDialog}
        confirmText={tProducts("linkedWsDialog.close")}
        confirmColor="primary"
        maxWidth="sm"
        fullWidth
        extraActions={
          bulkCentralLoading &&
          !centralLinkedJob?.cancelRequested &&
          centralLinkedJob?.status !== "cancel_requested" ? (
            <Button
              color="inherit"
              onClick={async () => {
                try {
                  const result = await cancelCentralLinkedImportJobFlow();
                  if (String(result?.status || "") !== "cancelled") {
                    setSnack({
                      open: true,
                      severity: "info",
                      message: tProducts("linkedWsDialog.cancelRequested"),
                    });
                  }
                } catch (err) {
                  setSnack({
                    open: true,
                    severity: "error",
                    message:
                      err?.message ||
                      err?.response?.data?.message ||
                      tProducts("linkedWsDialog.cancelFailed"),
                  });
                }
              }}
            >
              {tProducts("linkedWsDialog.cancel")}
            </Button>
          ) : null
        }
      />

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
      />
    </Box>
  );
};

export default Products;
